import { authFetch, BASE_URLS, getRequest, parkHttp, postRequest } from '@/lib/axios'
import { getClientDeviceId } from '@/lib/device-id'
import type { KioskLoginResult } from '@/types/auth'
import type {
  BoothChannel,
  BoothDutyClaimResult,
  BoothDutyCollectorOption,
  BoothDutyStatus,
  BoothLaneOccupancy,
  BoothManualConfirmItem,
  ParkRecord,
} from '@/types/booth'
import type { ApiPayload } from '@/lib/api-response'
import { extractPageList, getApiData } from '@/lib/api-response'

export async function loginApi(username: string, password: string) {
  const res = await postRequest<ApiPayload>(
    '/auth/login',
    { username, password, deviceId: getClientDeviceId(), clientType: 'kiosk' },
    { skipAuthExpiredRedirect: true, validateStatus: (status) => status < 500 },
  )
  return res.data
}

export async function exchangeKioskAuthCode(authCode: string) {
  const res = await postRequest<ApiPayload>(
    '/auth/exchange-token',
    { authCode },
    { skipAuthExpiredRedirect: true, validateStatus: (status) => status < 500 },
  )
  return res.data
}

export async function fetchUserInfo() {
  const res = await getRequest<ApiPayload>('/auth/me')
  return res.data
}

export async function logoutKiosk() {
  const res = await postRequest<ApiPayload>('/auth/logout', { deviceId: getClientDeviceId() })
  return res.data
}

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  const list = (data as { list?: T[] } | null)?.list
  return Array.isArray(list) ? list : []
}

export async function fetchBoothChannels(parkingId: string): Promise<BoothChannel[]> {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/channels', { parkingId })
  return asList<BoothChannel>(res.data?.data)
}

export async function fetchBoothManualConfirmPending(parkingId: string): Promise<BoothManualConfirmItem[]> {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/manualConfirm/pending', { parkingId })
  return asList<BoothManualConfirmItem>(res.data?.data)
}

export async function decideBoothManualConfirm(params: {
  parkingId: string
  confirmId: string
  approved: boolean
}) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/manualConfirm/decide', {
    parkingId: params.parkingId,
    manualConfirmId: params.confirmId,
    action: params.approved ? 'allow' : 'reject',
  })
  return res.data
}

export async function openBoothGate(params: { parkingId: string; channelId: string; plateNo?: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/gate/open', params)
  return res.data
}

export async function closeBoothGate(params: { parkingId: string; channelId: string; plateNo?: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/gate/close', params)
  return res.data
}

export async function pauseBoothGate(params: {
  parkingId: string
  channelId: string
  plateNo?: string
  paused?: boolean
}) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/gate/pause', params)
  return res.data
}

export async function parkBoothSnapshot(params: { parkingId: string; channelId: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/snapshot', params)
  return res.data
}

/** 手动入场实时抓拍（视捷真抓拍，非阻塞返回 captureId，图片经 SSE 推送回显） */
export async function parkBoothManualCapture(params: { parkingId: string; channelId: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/manualCapture', params, { timeout: 20000 })
  return res.data
}

/** 手动抓拍结果查询（SSE 超时后轮询 Redis DONE） */
export async function parkBoothManualCaptureResult(captureId: string) {
  const res = await parkHttp.get<ApiPayload>('/parkBooth/manualCapture/result', {
    params: { captureId },
    timeout: 8000,
  })
  return res.data
}

export async function parkBoothVideo(params: { parkingId: string; channelId: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/video', params)
  return res.data
}

export async function parkBoothEventsPage(params: {
  parkingId: string
  eventType?: string
  pageNo?: number
  pageSize?: number
  startTime?: string
  endTime?: string
}) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/events/page', {
    parkingId: params.parkingId,
    eventType: params.eventType,
    startTime: params.startTime,
    endTime: params.endTime,
    page: { pageNo: params.pageNo ?? 1, pageSize: params.pageSize ?? 50 },
  })
  return res.data
}

export async function fetchBoothPassRecords(params: {
  parkingId: string
  pageNo: number
  pageSize: number
  /** 出场时间窗口（当天：out_time 范围） */
  startTime: string
  endTime: string
  /** 在场侧入场时间窗口（近3天入场的在场车），不传则返回全部在场 */
  inTimeStart?: string
  inTimeEnd?: string
}): Promise<{ list: ParkRecord[]; total: number }> {
  try {
    const res = await parkHttp.post<ApiPayload>('/parkRecord/page', {
      parkingId: params.parkingId,
      includePresentAndWindowedExit: true,
      outTimeStart: params.startTime,
      outTimeEnd: params.endTime,
      inTimeStart: params.inTimeStart,
      inTimeEnd: params.inTimeEnd,
      page: { pageNo: params.pageNo, pageSize: params.pageSize },
    })
    const pageData = res.data?.data as { list?: ParkRecord[]; records?: ParkRecord[]; total?: number } | ParkRecord[]
    if (Array.isArray(pageData)) return { list: pageData, total: pageData.length }
    const list = pageData?.list ?? pageData?.records ?? []
    return { list, total: pageData?.total ?? list.length }
  } catch {
    const res = await parkHttp.post<ApiPayload>('/parkBooth/events/page', {
      parkingId: params.parkingId,
      page: { pageNo: params.pageNo, pageSize: params.pageSize },
      startTime: params.startTime,
      endTime: params.endTime,
    })
    const pageData = res.data?.data as { list?: ParkRecord[]; records?: ParkRecord[]; total?: number } | ParkRecord[]
    if (Array.isArray(pageData)) return { list: pageData, total: pageData.length }
    const list = pageData?.list ?? pageData?.records ?? []
    return { list, total: pageData?.total ?? list.length }
  }
}

/** 按车牌查当前在场记录（不限入场时间）。岗亭进出记录默认只带近 72 小时入场的在场车，长停车辆需单独补查。 */
export async function fetchPresentParkRecord(params: {
  parkingId: string
  plateNo?: string
}): Promise<ParkRecord | undefined> {
  const plate = params.plateNo?.trim()
  if (!params.parkingId || !plate) return undefined
  const res = await parkHttp.post<ApiPayload>('/parkRecord/page', {
    parkingId: params.parkingId,
    presentOnly: true,
    plateNo: plate,
    page: { pageNo: 1, pageSize: 10 },
  })
  const pageData = res.data?.data as { list?: ParkRecord[]; records?: ParkRecord[] } | ParkRecord[]
  const list = Array.isArray(pageData) ? pageData : (pageData?.list ?? pageData?.records ?? [])
  return (
    list.find((row) => row.plateNo?.trim() === plate && !row.outTime?.trim())
    || list.find((row) => !row.outTime?.trim())
    || list[0]
  )
}

export async function parkVehicleTypePage(parkingId: string) {
  const res = await parkHttp.post<ApiPayload>('/parkVehicleType/page', {
    parkingId,
    page: { pageNo: 1, pageSize: 500 },
  })
  return res.data
}

export async function parkRecordAdd(params: Record<string, unknown>) {
  const res = await parkHttp.post<ApiPayload>('/parkRecord/addParkRecordByHand', params)
  return res.data
}

export async function parkRecordOutAdd(params: Record<string, unknown>) {
  const res = await parkHttp.post<ApiPayload>('/parkRecord/addParkRecordOutByHand', params)
  return res.data
}

export async function parkRecordUserOutOrder(params: Record<string, unknown>, open: boolean) {
  const res = await parkHttp.post<ApiPayload>(`/parkRecord/user/out/order?open=${open}`, params)
  return res.data
}

/** 取消出口待处理（误操作手动出场等）：作废未支付出场单，车辆回到在场 */
export async function parkRecordUserOutOrderCancel(params: { parkRecordId?: string; orderId?: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkRecord/user/out/order/cancel', params)
  return res.data
}

export async function parkRecordQueryFee(params: { parkRecordId: string; parkingId?: string; plateNo?: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkRecord/parkfee', params)
  return res.data
}

export async function parkRecordOrderList(parkRecordId: string) {
  const res = await parkHttp.get<ApiPayload>('/parkRecord/order-list', { params: { parkRecordId } })
  return res.data
}

export async function parkChannelDisplayConfigGet(params: { channelId: string; parkingId?: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkChannel/displayConfig/get', params)
  return res.data
}

export async function parkRecordHomeStatistics(parkingId: string) {
  const res = await parkHttp.post<ApiPayload>('/parkRecord/homeStatistics', { parkingId })
  return res.data
}

export async function parkBoothCollectorDaily(params: { parkingId: string; date: string; collector?: string }) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/collector/daily', params)
  return res.data
}

export async function parkBoothShiftPage(params: Record<string, unknown>) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/shift/page', params)
  return res.data
}

export async function claimBoothDuty(params: {
  parkingId: string
  deviceId?: string
  sessionId?: string
  reLogin?: boolean
}): Promise<{ payload: ApiPayload; data: BoothDutyClaimResult | null }> {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/claim', {
    ...params,
    deviceId: params.deviceId || getClientDeviceId(),
  })
  return { payload: res.data, data: (res.data?.data ?? null) as BoothDutyClaimResult | null }
}

export async function heartbeatBoothDuty(params: {
  parkingId: string
  sessionId: string
  deviceId?: string
}) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/heartbeat', {
    ...params,
    deviceId: params.deviceId || getClientDeviceId(),
  })
  return res.data
}

export async function releaseBoothDuty(params: {
  parkingId: string
  sessionId: string
  deviceId?: string
}) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/release', {
    ...params,
    deviceId: params.deviceId || getClientDeviceId(),
  })
  return res.data
}

export async function releaseBoothDutyKeepalive(params: {
  parkingId: string
  sessionId: string
  deviceId?: string
}) {
  // 页面关闭（pagehide）时尽力释放值守会话：keepalive 请求不阻塞页面卸载，
  // 避免关窗后账号仍被“该账号正在上班中”拦截，无法直接重新登录；失败则依赖会话 TTL 自动释放
  try {
    const url = `${BASE_URLS.parkBase.replace(/\/$/, '')}/parkBooth/duty/release`
    await authFetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...params, deviceId: params.deviceId || getClientDeviceId() }),
    })
  } catch {
    // 尽力而为
  }
}

export async function fetchBoothDutyStatus(parkingId: string): Promise<BoothDutyStatus | null> {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/status', { parkingId })
  return (res.data?.data ?? null) as BoothDutyStatus | null
}

export async function fetchBoothDutyCollectors(params: {
  parkingId: string
  sessionId?: string
  deviceId?: string
}): Promise<BoothDutyCollectorOption[]> {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/collectors', {
    ...params,
    deviceId: params.deviceId || getClientDeviceId(),
  })
  return asList<BoothDutyCollectorOption>(res.data?.data)
}

export async function handoverBoothDuty(params: {
  parkingId: string
  sessionId: string
  targetUserId: string
  password?: string
  deviceId?: string
}) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/handover', {
    ...params,
    deviceId: params.deviceId || getClientDeviceId(),
  })
  return res.data
}

export async function clockOutBoothCollector(params: {
  parkingId: string
  sessionId: string
  targetUserId: string
  password?: string
  deviceId?: string
}) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/clockOutCollector', {
    ...params,
    deviceId: params.deviceId || getClientDeviceId(),
  })
  return res.data
}

export async function fetchBoothChannelOccupancy(params: {
  parkingId: string
  sessionId?: string
}): Promise<Record<string, BoothLaneOccupancy>> {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/channelOccupancy', {
    ...params,
    deviceId: getClientDeviceId(),
  })
  const data = res.data?.data
  const map: Record<string, BoothLaneOccupancy> = {}
  const rows = Array.isArray(data)
    ? (data as { channelId?: string; occupied?: boolean; self?: boolean; collectorName?: string }[])
    : asList<{ channelId?: string; occupied?: boolean; self?: boolean; collectorName?: string }>(data)
  for (const row of rows) {
    if (!row?.channelId) continue
    map[String(row.channelId)] = {
      occupied: row.occupied,
      self: row.self,
      collectorName: row.collectorName,
    }
  }
  return map
}

export async function assignBoothChannels(params: {
  parkingId: string
  sessionId: string
  channelIds: string[]
}) {
  const res = await parkHttp.post<ApiPayload>('/parkBooth/duty/assignChannels', {
    ...params,
    deviceId: getClientDeviceId(),
  })
  return res.data
}

export async function parkChannelPage(params: Record<string, unknown>) {
  const res = await parkHttp.post<ApiPayload>('/parkChannel/page', params)
  return res.data
}

export async function parkCameraRecognitionRecordPage(params: Record<string, unknown>) {
  const res = await parkHttp.post<ApiPayload>('/parkCamera/recognitionRecord/page', params)
  return res.data
}

export async function parkMemberPage(params: Record<string, unknown>) {
  const res = await parkHttp.post<ApiPayload>('/parkMember/page', params)
  return res.data
}

export async function parkInternalVehiclePage(params: Record<string, unknown>) {
  const res = await parkHttp.post<ApiPayload>('/parkInternalVehicle/page', params)
  return res.data
}

export function normalizeLoginUser(data: KioskLoginResult) {
  const loginAccount = data.username || data.loginAccount || ''
  const employeeName = data.nickname || data.employeeName || loginAccount
  return {
    username: loginAccount,
    name: employeeName,
    id: data.userId != null ? String(data.userId) : undefined,
    roles: data.roles,
  }
}

export function extractKioskSession(data: KioskLoginResult | null | undefined) {
  const token = (data?.accessToken || data?.token || '').trim()
  const parkingId = data?.parkingId != null ? String(data.parkingId).trim() : ''
  const parkName = data?.parkName?.trim() || ''
  return { token, parkingId, parkName }
}

export { extractPageList, getApiData }
